import {ReactNode, useCallback} from 'react';

import {brandT} from '@/lib/types/brand.types';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {
  BaseContextT,
  createAppContext,
  createAppHook,
  createAppProvider,
  createProviderConfig,
  useBaseContext,
} from '@/providers/components/baseProvider';

// Config
const {table, columns, statsView} = createProviderConfig({
  table: 'Brand',
  statsView: 'brand_stats',
});

type TableT = typeof table;
type ColumnsT = typeof columns;
type StatsViewT = typeof statsView;
type CustomContextT = {
  getBrand: (id: number) => Promise<brandT>;
};
// Context
type BrandContextT = BaseContextT<TableT, StatsViewT, ColumnsT, CustomContextT>;
const BrandsContext = createAppContext<BrandContextT>(table);

// Provider
const ContextProvider = ({children}: {children: ReactNode}) => {
  const {isAdministrator} = useAuth();
  const baseContext = useBaseContext<TableT, ColumnsT, StatsViewT>();

  /*******************************************************************
   * Gets a brand record from the database.
   * @param {number} id - The ID of the brand to get.
   * @returns {Promise<retailerT | null>} - The brand object.
   * @throws {Error} - If there's an error in getting the brand.
   ********************************************************************/
  const getBrand = useCallback(
    async (id: number) => {
      if (!isAdministrator) throw new Error('Error getting retailer');
      try {
        const {data, error} = await SupabaseInstance.supabase.from('Brand').select().eq('brand_id', id).single();
        if (!data || error) throw new Error('Brand not found');
        return data;
      } catch (error) {
        throw new Error((error as Error).message);
      }
    },
    [isAdministrator]
  );

  return (
    <BrandsContext.Provider
      value={{
        ...baseContext,
        getBrand,
      }}
    >
      {children}
    </BrandsContext.Provider>
  );
};

// Add this to the AppProviders.tsx file
export const BrandsProvider = createAppProvider({
  table,
  columns,
  statsView,
  ContextProvider,
});

/*******************************************************************
 * Hook
 *******************************************************************/
export const useBrands = createAppHook<TableT, StatsViewT, ColumnsT, CustomContextT>(BrandsContext);
